import { Component, OnInit } from '@angular/core';
import {
  CandidateDataLoadState,
  CandidateResultDto,
  DetailClient,
  ElectionAreaNavigationItemDto,
  ElectionClient,
  ElectionDto,
  ElectionType,
  ExportDataClient,
  FileResponse,
  IndependentCandidateDto,
  OverviewClient,
  PartyDetailDto,
  ResultSummaryDto,
  ResultType,
  VoteCountStatusDto,
  VotingDetailOutsideDto
} from '@shared/clients/api.generated';
import { getMapAreaStyle } from '@shared/components/map/map-styles.model';
import { SidenavigationLink } from '@shared/components/sidenavigation/sidenavigation.model';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { StatusPollingDistrict, StatusPollingDistrictColumnDef } from './nomination-district.model';
import { TopCandidates } from '@shared/components/top-candidates/top-candidates.component';
import { CountStatus } from '@shared/components/count-status/count-status.component';
import { PartyOrCandidateListBase, } from '@shared/components/expandable-party-list/expandable-party-table.model';
import { VotingDetails, VotingDetailsList } from '@shared/components/voting-details/voting-details.model';
import { SidenavigationLinkType } from '@shared/components/page-layout/page-layout.model';
import { ReferendumStatus } from '@shared/components/cards/referendum-status/referendum-status.model';
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { saveFile } from "@valg-shared/utils/file-utils";
import { Observable } from "rxjs";
import { SpinnerService } from "@valg-shared/services/spinner/spinner.service";
import { ExportFileService } from "@valg-shared/services/export-file/export-file.service";
import { ElectionOverviewService } from 'app/pages/election-overview/election-overview.service';

@Component({
  selector: 'valg-dk-details-nomination-district',
  templateUrl: './nomination-district.component.html',
  providers: [ SpinnerService ]
})
export class DetailsNominationDistrictComponent implements OnInit {

  election?: ElectionDto;
  nominationDistrictName = "";

  parties: PartyOrCandidateListBase[] = [];

  statusPollingDistrictColumnDef = StatusPollingDistrictColumnDef;
  statusPollingDistrict: StatusPollingDistrict[] = [];

  votingDetailsList: VotingDetailsList[] = [];
  votingDetails?: VotingDetails;
  resultType: ResultType = ResultType.NoResults;

  readonly SvgIcon = SvgIcon;

  links: SidenavigationLink[] = [];
  filterLinks: SidenavigationLink[] = [];
  sideNavTitleType = SidenavigationLinkType.PollingDistrict;
  parentLinkType = SidenavigationLinkType.Municipality;

  countStatus: CountStatus[] = [];
  countStatusUpdated: Date | undefined;

  referendumStatus: ReferendumStatus[] = [];
  topCandidates: TopCandidates[] = [];
  resultSummary!: ResultSummaryDto;

  showPartiesTable = true;
  showReferendumStatus = true;


  nominationDistrictId: string;
  candidateDataLoadState: CandidateDataLoadState = CandidateDataLoadState.NotLoaded;

  constructor(
    route: ActivatedRoute,
    private readonly detailClient: DetailClient,
    private readonly overviewClient: OverviewClient,
    private readonly electionClient: ElectionClient,
    private readonly translateService: TranslateService,
    private readonly exportDataClient: ExportDataClient,
    private readonly spinner: SpinnerService,
    private readonly exportFileService: ExportFileService,
    private readonly electionOverviewService: ElectionOverviewService
  ) {
    this.nominationDistrictId = route.snapshot.params['nominationDistrictId'];
  }

  ngOnInit(): void {
    this.checkContentAuthorization();
    this.initializeSideNavMenu();
    this.initialize();
  }

  private setParties(partyDetailDto?: PartyDetailDto[], independentCandidateNameList?: IndependentCandidateDto[] | null) {
    this.parties = [
      ...partyDetailDto?.map(x => {
        return {
          id: x.partyId,
          title: x.partyWholeName,
          votes: this.resultType == ResultType.NoResults ? "" : x.totalOfVotes,
          diff: x.difference,
          percentage: x.percentage,
          percentage_diff: x.percentageDifference,
          listOrganisation: x.listOrganisation,
          hasSpecifiedPersonalVotes: x.hasSpecifiedPersonalVotes,
          children: [
            {
              id: x.partyId,
              name: this.election?.electionType === ElectionType.GeneralElection || ElectionType.EuropeanParliamentElection ?
                this.translateService.instant("simple_text.party_list") : this.translateService.instant("simple_text.candidate_list"),
              votes: x.preVotes
            },
            ...x.candidateDetailDtos?.map(cr => {
              return {
                id: cr.candidateId,
                name: cr.name,
                votes: cr.candidateVotes
              }
            }) || []
          ]
        }
      }) || [],
      ...(independentCandidateNameList ? [ {
        title: this.translateService.instant("simple_text.independent_candidate"),
        children: independentCandidateNameList.map(ic => {
          return {
            name: ic.name,
            votes: ic.vote
          }
        })
      } ] : [])
    ] as PartyOrCandidateListBase[] || [];
  }

  private setTopCandidates(candidateResultDto: CandidateResultDto[] | undefined) {
    const highestVotesTopCandidate = candidateResultDto && candidateResultDto.length > 0
      ? candidateResultDto[0].numberOfVotes : null;

    this.topCandidates = candidateResultDto?.map(x => {
      return {
        name: x.displayName,
        votes: x.numberOfVotes!,
        percentage: highestVotesTopCandidate && highestVotesTopCandidate !== 0 ? (x.numberOfVotes! / highestVotesTopCandidate)*100 : 0
      }
    }) || [];
  }

  private getGeNominationDistrictDetail() {
    this.detailClient.getGeNominationDistrictDetail(this.nominationDistrictId).subscribe((result) => {
      this.candidateDataLoadState = result.candidateDataLoadState;
      this.nominationDistrictName = result.nominationDistrictName;
      this.resultType = result.resultType;
      this.resultSummary = result.resultSummaryDto;
      this.setVoteCountStatus(result.voteCountStatusDto);
      this.setVotingDetails(result.votingDetailOutsideDto);
      this.setStatusPollingDistrict(result.electionAreaDetailDto);
      this.setParties(result.partyDetailDto, result.independentCandidateNameList);
      this.setTopCandidates(result.candidateResultDto);
    })
  }

  private getEvNominationDistrictDetail() {
    this.detailClient.getEvNominationDistrictDetail(this.nominationDistrictId).subscribe((result) => {
      this.nominationDistrictName = result.nominationDistrictName;
      this.resultType = result.resultType;
      this.resultSummary = result.resultSummaryDto;
      this.setVoteCountStatus(result.voteCountStatusDto);
      this.setVotingDetails(result.votingDetailOutsideDto);
      this.setStatusPollingDistrict(result.electionAreaDetailDto);
      this.setParties(result.partyDetailDto, null);
      this.setTopCandidates(result.candidateResultDto);
      this.candidateDataLoadState = result.candidateDataLoadState;
    })
  }



  private getFaNominationDistrictDetail() {
    this.detailClient.getFaNominationDistrictDetail(this.nominationDistrictId).subscribe((result) => {
      this.resultType = result.resultType;
      this.candidateDataLoadState = result.candidateDataLoadState;
      this.nominationDistrictName = result.nominationDistrictName;
      this.resultSummary = result.resultSummaryDto;
      this.resultType = result.resultType;
      this.setVoteCountStatus(result.voteCountStatusDto);
      this.setVotingDetails(result.votingDetailOutsideDto);
      this.referendumStatus = result.faElectionOverviewDto? [
        {
          option: this.translateService.instant("simple_text.yes"),
          votes: result.faElectionOverviewDto?.yesVotes,
          percentage: result.faElectionOverviewDto?.yesPercentage
        },
        {
          option: this.translateService.instant("simple_text.no"),
          votes: result.faElectionOverviewDto?.noVotes,
          percentage: result.faElectionOverviewDto?.noPercentage
        }
      ] as ReferendumStatus[] : [];
    })
  }

  private setStatusPollingDistrict(electionAreaDetailDto?: ElectionAreaNavigationItemDto[]) {
    this.statusPollingDistrict = electionAreaDetailDto?.map(x => {
      return {
        id: x.id,
        nominationDistrict: x.name,
        status: getMapAreaStyle(x.resultType)
      }
    }) || [];
  }

  private setVoteCountStatus(voteCountStatusDto?: VoteCountStatusDto) {
    this.countStatusUpdated = voteCountStatusDto?.recentUpdateTimestamp;
    this.countStatus = [
      {
        name: "count_status.voting_percentage",
        percentage: voteCountStatusDto?.eligibleVoterPercentage
      },
      {
        name: "count_status.voting_prec_count_percentage",
        percentage: voteCountStatusDto?.preCountPercentage
      },
      {
        name: "count_status.voting_fine_count_percentage",
        percentage: voteCountStatusDto?.fineCountPercentage
      }
    ];
  }

  private setVotingDetails(votingDetailOutsideDto?: VotingDetailOutsideDto) {
    this.votingDetails = {
      numberOfVoters: votingDetailOutsideDto?.numberOfEligibleVoters ?? 0,
      countedPollingAreas: {
        number: votingDetailOutsideDto?.numberOfElectionAreasWithPreliminary ?? 0,
        total: votingDetailOutsideDto?.numberOfElectionAreas ?? 0,
      },
      validVotes: {
        value: votingDetailOutsideDto?.validVotes ?? 0,
        diff: votingDetailOutsideDto?.validVotesPreviousElectionDifference
      },
      invalidVotes: {
        value: votingDetailOutsideDto?.invalidVotes ?? 0,
        diff: votingDetailOutsideDto?.invalidVotesPreviousElectionDifference
      },
      blankVotes: {
        value: votingDetailOutsideDto?.blankVotes ?? 0,
        diff: votingDetailOutsideDto?.blankVotesPreviousElectionDifference
      },
      totalVotes: {
        value: votingDetailOutsideDto?.totalVotes ?? 0,
        diff: votingDetailOutsideDto?.totalVotesPreviousElectionDifference
      }
    }
  }



  initialize() {
    this.electionClient.getCurrentElection().subscribe((result) => {
      this.election = {
        "id": result.id,
        "electionName": result.electionName,
        "electionDate": result.electionDate,
        "electionType": result.electionType,
        "title": result.electionType === ElectionType.Referendum ? result.referendumTitle : undefined
      };
      this.checkReferendumStatusAuthorization();

      switch(result.electionType) {
        case ElectionType.GeneralElection:
          this.getGeNominationDistrictDetail();
          break;
        case ElectionType.EuropeanParliamentElection:
          this.getEvNominationDistrictDetail();
          break;
        case ElectionType.Referendum:
          this.getFaNominationDistrictDetail();
          break;
      }
    })
  }
  private initializeSideNavMenu() {
    this.overviewClient.getNominationDistrictDetailMenu(this.nominationDistrictId).subscribe((result) => {
      this.links = this.electionOverviewService.mapNominationDistrict(result, this.nominationDistrictId);
    });

    this.overviewClient.getElectionSideNavigation().subscribe((result) => {
      this.filterLinks = this.electionOverviewService.mapConstituencies(result);
    });
  }


  checkContentAuthorization(){
    this.checkPartiesTableAuthorization();
    this.checkReferendumStatusAuthorization();
  }

  checkReferendumStatusAuthorization(){
    if(this.election){
      let authorizedElections: ElectionType[] = [
        ElectionType.Referendum
      ];

      this.showReferendumStatus =  authorizedElections.includes(this.election?.electionType);
    }
  }

  checkPartiesTableAuthorization(){
    if(this.election){
      let authorizedElections: ElectionType[] = [
        ElectionType.GeneralElection,
        ElectionType.RegionalElection,
        ElectionType.MunicipalElection,
        ElectionType.EuropeanParliamentElection
      ];

      this.showPartiesTable = authorizedElections.includes(this.election?.electionType);
    }
  }

  onDownloadReportClick() {
    const handler = this.getExportDataCsv();
    this.spinner.attachSpinner(handler).subscribe(result => {
      const fileName = this.exportFileService.getReportFileNameWithDate(result.fileName, ".csv");
      saveFile(result.data, fileName);
    });
  }
  private getExportDataCsv(): Observable<FileResponse> {
    const currentElection = this.election;
    switch (currentElection?.electionType) {
      case ElectionType.GeneralElection:
        return this.exportDataClient.exportFvDataCsv(currentElection?.id, null,
          this.nominationDistrictId, null);
      case ElectionType.EuropeanParliamentElection:
        return this.exportDataClient.exportEvDataCsv(currentElection?.id, null,
          this.nominationDistrictId, null);
      case ElectionType.Referendum:
        return this.exportDataClient.exportFaDataCsv(currentElection?.id, null,
          this.nominationDistrictId, null);
      default:
        throw new Error('Election type is not found.');
    }
  }
}
